import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Wallet, SignOut, Cpu, Sun, Moon, List, X, ArrowRight, Copy, CaretDown } from "@phosphor-icons/react";
import AegisLogo from "@/components/AegisLogo";
import MegaMenu from "@/components/MegaMenu";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NAV } from "@/constants/testIds";
import { fmtAone, shortAddr } from "@/lib/api";
import { toast } from "sonner";

const MOBILE_LINKS = [
    { to: "/", label: "Home", tid: "nav-home", end: true },
    { to: "/miner", label: "Miner Terminal", tid: "nav-miner" },
    { to: "/wallet", label: "Wallet", tid: "nav-wallet-page" },
    { to: "/wallet/withdraw", label: "Withdraw USDT", tid: "nav-withdraw" },
    { to: "/referrals", label: "Referrals", tid: "nav-referrals" },
    { to: "/swap", label: "Swap", tid: "nav-swap" },
    { to: "/validators", label: "Validators", tid: "nav-validators" },
    { to: "/tokenomics", label: "Tokenomics", tid: "nav-tokenomics" },
];

export default function TopNav({ onOpenAuth }) {
    const { user, logout } = useAuth();
    const { theme, toggle: toggleTheme } = useTheme();
    const nav = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => { setMenuOpen(false); }, [location.pathname]);
    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    return (
        <>
            <header
                className="sticky top-0 z-40 border-b nav-blur"
                style={{ borderColor: "var(--border)" }}
            >
                <div className="max-w-[1440px] mx-auto flex items-center gap-4 px-5 sm:px-8 h-[60px]">
                    {/* Brand */}
                    <NavLink to="/" data-testid={NAV.logo} className="flex items-center gap-2.5 shrink-0 group">
                        <AegisLogo size={28} />
                        <div className="leading-none whitespace-nowrap">
                            <div className="text-[16px] font-semibold tracking-[-0.02em] text-[var(--text)]">Aegis One</div>
                        </div>
                    </NavLink>

                    {/* Mega menu */}
                    <MegaMenu />

                    {/* Right controls */}
                    <div className="ml-auto flex items-center gap-2 shrink-0">
                        <button
                            onClick={toggleTheme}
                            data-testid="nav-theme-toggle"
                            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                            className="inline-flex p-2 rounded-md text-[var(--text-3)] hover:text-[var(--text)] hover:bg-[var(--bg-surface)] transition-colors"
                        >
                            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
                        </button>

                        {user ? (
                            <>
                                <NavLink
                                    to="/miner"
                                    data-testid="nav-miner-open-signed"
                                    className="!hidden xl:!inline-flex btn-ghost !py-2 !px-3 !text-[13px]"
                                >
                                    <Cpu size={13} /> Mine
                                </NavLink>
                                <NavLink
                                    to="/wallet"
                                    data-testid="nav-wallet-open-signed"
                                    className="!hidden xl:!inline-flex btn-ghost !py-2 !px-3 !text-[13px]"
                                >
                                    <Wallet size={13} /> Wallet
                                </NavLink>
                                <UserMenu user={user} onLogout={logout} />
                            </>
                        ) : (
                            <>
                                <NavLink
                                    to="/wallet"
                                    data-testid="nav-wallet-open"
                                    className="!hidden xl:!inline-flex btn-ghost !py-2 !px-3 !text-[13px]"
                                >
                                    <Wallet size={13} /> Wallet
                                </NavLink>
                                <button
                                    data-testid="nav-signin"
                                    onClick={onOpenAuth}
                                    className="!hidden xl:!inline-flex btn-ghost !py-2 !px-3 !text-[13px]"
                                >
                                    Sign in
                                </button>
                                <button
                                    data-testid={NAV.connect}
                                    onClick={onOpenAuth}
                                    className="!hidden xl:!inline-flex btn-primary !py-2 !px-4 !text-[13px]"
                                >
                                    Start Mining
                                    <ArrowRight size={12} />
                                </button>
                            </>
                        )}

                        <MobileMenuButton open={menuOpen} setOpen={setMenuOpen} />
                    </div>
                </div>
            </header>

            {/* Mobile drawer (below xl) */}
            {menuOpen && (
                <div
                    className="xl:hidden fixed inset-0 z-50"
                    style={{ background: "rgba(10,10,10,0.35)" }}
                    onClick={() => setMenuOpen(false)}
                    data-testid="mobile-menu-overlay"
                >
                    <aside
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-[86%] max-w-[380px] overflow-y-auto"
                        style={{ background: "var(--bg)", boxShadow: "var(--shadow-xl)" }}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
                            <div className="flex items-center gap-3">
                                <AegisLogo size={26} />
                                <div className="leading-none">
                                    <div className="text-[16px] font-semibold text-[var(--text)]">Aegis One</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={toggleTheme}
                                    data-testid="mobile-theme-toggle"
                                    title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                                    className="p-2 rounded-md text-[var(--text-3)] hover:text-[var(--text)] hover:bg-[var(--bg-surface)]"
                                >
                                    {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
                                </button>
                                <button
                                    onClick={() => setMenuOpen(false)}
                                    data-testid="mobile-menu-close"
                                    className="p-2 rounded-md text-[var(--text-3)] hover:text-[var(--text)] hover:bg-[var(--bg-surface)]"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <nav className="p-3">
                            {MOBILE_LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.end}
                                    data-testid={`m-${l.tid}`}
                                    className={({ isActive }) =>
                                        `block px-4 py-2.5 rounded-md text-[14px] font-medium transition-colors ${
                                            isActive
                                                ? "text-[var(--primary)] bg-[var(--primary-soft)]"
                                                : "text-[var(--text)] hover:bg-[var(--bg-surface)]"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </nav>
                        {!user && (
                            <div className="p-5 border-t space-y-2" style={{ borderColor: "var(--border)" }}>
                                <button
                                    onClick={() => { setMenuOpen(false); onOpenAuth?.(); }}
                                    data-testid="mobile-connect"
                                    className="btn-primary w-full justify-center !py-2.5 !text-[13.5px]"
                                >
                                    Start Mining <ArrowRight size={12} />
                                </button>
                                <button
                                    onClick={() => { setMenuOpen(false); onOpenAuth?.(); }}
                                    data-testid="mobile-signin"
                                    className="btn-ghost w-full justify-center !py-2.5 !text-[13px]"
                                >
                                    Sign in
                                </button>
                                <NavLink
                                    to="/wallet"
                                    onClick={() => setMenuOpen(false)}
                                    className="btn-ghost w-full justify-center !py-2.5 !text-[13px]"
                                >
                                    <Wallet size={13} /> Open Wallet
                                </NavLink>
                            </div>
                        )}
                        {user && (
                            <div className="p-5 border-t space-y-3" style={{ borderColor: "var(--border)" }}>
                                <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">
                                    Signed in
                                </div>
                                <div>
                                    <div className="text-[14px] font-semibold text-[var(--text)]">{user.display_name}</div>
                                    <div className="font-mono text-[11px] text-[var(--text-3)] mt-1">{shortAddr(user.address, 12, 8)}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 pt-2">
                                    <div>
                                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">AONE</div>
                                        <div className="font-mono text-[14px] text-[var(--text)] mt-0.5">{fmtAone(user.aone_balance)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">USDT</div>
                                        <div className="font-mono text-[14px] text-[var(--text)] mt-0.5">${fmtAone(user.usdt_balance || 0)}</div>
                                    </div>
                                </div>
                                <div className="flex gap-2 pt-3">
                                    <NavLink to="/miner" onClick={() => setMenuOpen(false)} className="btn-primary flex-1 !text-[13px] justify-center">
                                        <Cpu size={13} /> Mine
                                    </NavLink>
                                    <button onClick={logout} className="btn-ghost flex-1 !text-[13px] justify-center">
                                        <SignOut size={14} /> Sign out
                                    </button>
                                </div>
                            </div>
                        )}
                    </aside>
                </div>
            )}
        </>
    );
}

function MobileMenuButton({ open, setOpen }) {
    return (
        <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            className="xl:hidden p-2 rounded-md text-[var(--text-3)] hover:text-[var(--text)] hover:bg-[var(--bg-surface)] transition-colors"
        >
            {open ? <X size={17} /> : <List size={17} />}
        </button>
    );
}

/* Avatar → dropdown with account info + Sign out.
   Discoverable click-to-open menu (Stripe / Vercel / Linear pattern). */
function UserMenu({ user, onLogout }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const copyAddr = () => {
        if (!user?.address) return;
        navigator.clipboard.writeText(user.address);
        toast.success("Address copied");
    };

    const initial = (user.display_name || user.email || "A").slice(0, 1).toUpperCase();

    return (
        <div ref={ref} className="hidden md:block relative" data-testid={NAV.userMenu}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="nav-user-menu-trigger"
                title={user.display_name || user.email}
                className={`inline-flex items-center gap-2 pl-2 pr-2.5 py-1 ml-2 rounded-md border transition-colors ${
                    open
                        ? "border-[var(--border-strong)] bg-[var(--bg-surface)]"
                        : "border-transparent hover:bg-[var(--bg-surface)]"
                }`}
            >
                <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0"
                    style={{ background: "var(--primary)" }}
                >
                    {initial}
                </div>
                <div className="hidden xl:block leading-tight text-left">
                    <div className="text-[12px] font-medium text-[var(--text)] truncate max-w-[110px]">
                        {user.display_name || user.email}
                    </div>
                    <div className="font-mono text-[10px] text-[var(--text-3)] mt-0.5">
                        {fmtAone(user.aone_balance)} AONE
                    </div>
                </div>
                <CaretDown size={10} className={`text-[var(--text-3)] transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid="nav-user-menu-panel"
                    className="absolute right-0 top-full mt-2 w-[288px] mega-panel p-2 z-50 fade-in-up"
                >
                    {/* Header */}
                    <div className="px-3 py-2.5 border-b" style={{ borderColor: "var(--border)" }}>
                        <div className="text-[13.5px] font-semibold text-[var(--text)] truncate">
                            {user.display_name || "Account"}
                        </div>
                        <div className="text-[12px] text-[var(--text-3)] truncate">{user.email}</div>
                        <button
                            onClick={copyAddr}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono text-[var(--text-2)] hover:text-[var(--primary)] transition-colors"
                            title="Copy address"
                        >
                            {shortAddr(user.address, 10, 6)}
                            <Copy size={11} />
                        </button>
                    </div>

                    {/* Balance */}
                    <div className="px-3 py-2.5 border-b" style={{ borderColor: "var(--border)" }}>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.12em] font-mono text-[var(--text-3)]">AONE</div>
                                <div className="font-mono text-[14px] text-[var(--text)] mt-1">{fmtAone(user.aone_balance)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.12em] font-mono text-[var(--text-3)]">USDT</div>
                                <div className="font-mono text-[14px] text-[var(--text)] mt-1">${fmtAone(user.usdt_balance || 0)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Sign out */}
                    <div className="pt-1">
                        <button
                            data-testid={NAV.logout}
                            onClick={() => { setOpen(false); onLogout(); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-[13.5px] font-medium text-[var(--text)] hover:bg-[var(--bg-surface)] hover:text-[var(--primary)] transition-colors text-left"
                        >
                            <SignOut size={14} />
                            Sign out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
